import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

// Generic anchor-positioned popover, rendered into <body> via React
// portal so it spills cleanly out of clipped/overflowing parents (the
// SubBar in particular). Used by SamplesPicker; designed to be reused
// by Phase 3's MeasureTable cell editors.
//
// Position: opens directly below the anchor, flush-left, clamped to
// the viewport. Doesn't try to flip above — popover content keeps
// max-height: 70vh + overflow: auto, so vertical clipping is rare.
//
// Close affordances: click outside (excluding the anchor itself so
// clicking the trigger again toggles cleanly) and Escape key.
export default function PortalPopover({
  anchorRef,
  open,
  onClose,
  minWidth = 200,
  children,
}) {
  const popoverRef = useRef(null);
  const [pos, setPos] = useState(null);

  // Layout-effect so position is set before paint — without this the
  // first frame paints the popover at (0,0) before jumping to anchor.
  // Provisorisch linksbündig mit minWidth-Clamp; der echte Flip nach
  // rechts kommt nach dem Mount, wenn wir die tatsächliche Breite kennen.
  useLayoutEffect(() => {
    if (!open || !anchorRef.current) return;
    const r = anchorRef.current.getBoundingClientRect();
    setPos({
      top: r.bottom + 4,
      left: Math.max(8, Math.min(r.left, window.innerWidth - minWidth - 8)),
    });
  }, [open, anchorRef, minWidth]);

  // Flip-Korrektur: sobald der Popover gemountet ist, messen wir
  // seine echte Breite (kann minWidth deutlich übersteigen, z.B. bei
  // langen Werkstoff-Bezeichnungen in MaterialCell oder breiten
  // Norm-Drill-Downs in NormCell). Wenn er ab anchor.left rechts
  // überläuft, ankern wir stattdessen rechtsbündig zum Anchor — das
  // ist das Standard-Verhalten, das Browser-Native-Selects nutzen.
  // Setzen wir den State nur bei tatsächlicher Änderung, damit der
  // Effekt nicht infinite-loopt (pos-Update triggert erneuten Lauf).
  useLayoutEffect(() => {
    if (!open || !pos || !popoverRef.current || !anchorRef.current) return;
    const r = anchorRef.current.getBoundingClientRect();
    const w = popoverRef.current.offsetWidth;
    const rightEdge = pos.left + w;
    if (rightEdge > window.innerWidth - 8) {
      // Rechtsbündig zum Anchor; weiterhin mit 8 px Mindestabstand zum
      // linken Viewport-Rand, falls der Anchor selbst nahe links sitzt
      // und der Popover so breit ist, dass er trotz Flip nicht reinpasst.
      const flipped = Math.max(8, r.right - w);
      if (flipped !== pos.left) {
        setPos((p) => ({ ...p, left: flipped }));
      }
    }
  }, [open, pos, anchorRef]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (popoverRef.current?.contains(e.target)) return;
      if (anchorRef.current?.contains(e.target)) return;
      onClose();
    };
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, anchorRef, onClose]);

  if (!open || !pos) return null;
  return createPortal(
    <div
      ref={popoverRef}
      role="dialog"
      className="bg-surface border border-line-strong shadow-2xl"
      style={{
        position: 'fixed',
        top: pos.top,
        left: pos.left,
        minWidth,
        borderRadius: 'var(--radius-md)',
        maxHeight: '70vh',
        overflow: 'auto',
        zIndex: 1000,
      }}
    >
      {children}
    </div>,
    document.body
  );
}
